import React, { useState } from 'react';
import { Modal, Button } from '../../design-system';
import { Tag, Empty, Input, DatePicker, message, Button as AntButton, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, SaveOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { roleService } from '../../services/roleService';
import moment from 'moment';

const RateHistoryModal = ({ visible, role, rateHistory, onClose, onRefresh }) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [newRate, setNewRate] = useState('');
    const [newValidAt, setNewValidAt] = useState(moment());
    const [saving, setSaving] = useState(false);
    const [deletingRateId, setDeletingRateId] = useState(null);

    if (!role) return null;

    const handleAddRate = async () => {
        if (!newRate || parseFloat(newRate) <= 0) {
            message.error('Please enter a valid rate greater than 0');
            return;
        }

        if (!newValidAt) {
            message.error('Please select an effective date');
            return;
        }

        try {
            setSaving(true);
            await roleService.addRate(role.id, {
                rate: parseFloat(newRate),
                valid_at: newValidAt.format('YYYY-MM-DD')
            });

            message.success('Rate added successfully');
            setNewRate('');
            setNewValidAt(moment());
            setShowAddForm(false);

            if (onRefresh) {
                onRefresh(); // Refresh rate history
            }
        } catch (error) {
            message.error('Failed to add rate');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelAdd = () => {
        setNewRate('');
        setNewValidAt(moment());
        setShowAddForm(false);
    };

    const handleDeleteRate = async (rateId) => {
        try {
            setDeletingRateId(rateId);
            await roleService.deleteRate(role.id, rateId);
            message.success('Rate deleted successfully');

            if (onRefresh) {
                onRefresh(); // Refresh rate history
            }
        } catch (error) {
            if (error.response?.data?.code === 'LAST_RATE') {
                message.error('Cannot delete the only rate entry');
            } else {
                message.error('Failed to delete rate');
            }
        } finally {
            setDeletingRateId(null);
        }
    };

    return (
        <Modal visible={visible} onClose={onClose} width="medium">
            <Modal.Header title="Rate History" />
            <Modal.Body padding="default">
                {/* Role Info Card */}
                <div style={{
                    background: '#fafafa',
                    border: '1px solid #e8e8e8',
                    borderRadius: '4px',
                    padding: '16px',
                    marginBottom: '24px'
                }}>
                    <div style={{ marginBottom: '12px' }}>
                        <div style={{
                            fontSize: '12px',
                            color: '#8c8c8c',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '4px'
                        }}>
                            Role / Seniority Level
                        </div>
                        <h5 style={{
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#262626'
                        }}>
                            {role.name}
                        </h5>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e8e8e8'
                    }}>
                        <div>
                            <div style={{
                                fontSize: '12px',
                                color: '#8c8c8c',
                                marginBottom: '4px'
                            }}>
                                Department
                            </div>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#262626'
                            }}>
                                {role.department_name}
                            </div>
                        </div>
                        <div>
                            <div style={{
                                fontSize: '12px',
                                color: '#8c8c8c',
                                marginBottom: '4px'
                            }}>
                                Current Rate
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 600,
                                color: '#52c41a'
                            }}>
                                SGD {role.current_rate}/hour
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add New Rate Button */}
                {!showAddForm && (
                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setShowAddForm(true)}
                            size="small"
                        >
                            Add New Rate
                        </Button>
                    </div>
                )}

                {(rateHistory && rateHistory.length > 0) || showAddForm ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            marginTop: 16
                        }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                                    <th style={{
                                        padding: '12px 8px',
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontSize: '14px'
                                    }}>
                                        Public Rate (SGD)
                                    </th>
                                    <th style={{
                                        padding: '12px 8px',
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontSize: '14px'
                                    }}>
                                        Effective Date
                                    </th>
                                    <th style={{
                                        padding: '12px 8px',
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontSize: '14px'
                                    }}>
                                        Created By
                                    </th>
                                    <th style={{
                                        padding: '12px 8px',
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontSize: '14px'
                                    }}>
                                        Created At
                                    </th>
                                    <th style={{
                                        padding: '12px 8px',
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        width: '80px'
                                    }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Inline Add Form */}
                                {showAddForm && (
                                    <tr style={{
                                        borderBottom: '1px solid #f0f0f0',
                                        backgroundColor: '#fffbe6'
                                    }}>
                                        <td style={{ padding: '12px 8px' }}>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                prefix="SGD"
                                                suffix="/hour"
                                                placeholder="0.00"
                                                value={newRate}
                                                onChange={(e) => setNewRate(e.target.value)}
                                                style={{ width: '150px' }}
                                            />
                                        </td>
                                        <td style={{ padding: '12px 8px' }}>
                                            <DatePicker
                                                value={newValidAt}
                                                onChange={(date) => setNewValidAt(date)}
                                                format="YYYY-MM-DD"
                                                style={{ width: '150px' }}
                                            />
                                        </td>
                                        <td style={{ padding: '12px 8px' }}>
                                            <span style={{ color: '#8c8c8c' }}>-</span>
                                        </td>
                                        <td style={{ padding: '12px 8px' }}>
                                            <span style={{ color: '#8c8c8c' }}>-</span>
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                <Tooltip title="Save">
                                                    <AntButton
                                                        type="primary"
                                                        size="small"
                                                        icon={<SaveOutlined />}
                                                        onClick={handleAddRate}
                                                        loading={saving}
                                                    />
                                                </Tooltip>
                                                <Tooltip title="Cancel">
                                                    <AntButton
                                                        size="small"
                                                        icon={<CloseOutlined />}
                                                        onClick={handleCancelAdd}
                                                        disabled={saving}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {rateHistory.map((rate, index) => (
                                    <tr
                                        key={rate.id}
                                        style={{
                                            borderBottom: '1px solid #f0f0f0',
                                            backgroundColor: index === 0 ? '#e6f7ff' : 'white'
                                        }}
                                    >
                                        <td style={{ padding: '12px 8px' }}>
                                            <strong>SGD {rate.rate}/hour</strong>
                                            {index === 0 && (
                                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                                    Current
                                                </Tag>
                                            )}
                                        </td>
                                        <td style={{ padding: '12px 8px' }}>
                                            {new Date(rate.valid_at).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'numeric',
                                                day: 'numeric'
                                            })}
                                        </td>
                                        <td style={{ padding: '12px 8px' }}>
                                            {rate.created_by?.full_name || '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px' }}>
                                            {new Date(rate.created_at).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'numeric',
                                                day: 'numeric'
                                            })}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                            <Tooltip title={rateHistory.length === 1 ? 'Cannot delete the only rate' : 'Delete'}>
                                                <Popconfirm
                                                    title="Delete Rate"
                                                    description="Are you sure you want to delete this rate entry?"
                                                    onConfirm={() => handleDeleteRate(rate.id)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                    disabled={rateHistory.length === 1}
                                                >
                                                    <AntButton
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        size="small"
                                                        disabled={rateHistory.length === 1}
                                                        loading={deletingRateId === rate.id}
                                                    />
                                                </Popconfirm>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <Empty
                        description="No rate history available"
                        style={{ margin: '32px 0' }}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
};

export default RateHistoryModal;
